<script setup lang="ts">
  /**
   * Message row — 03-components/13f-message-list · OSCloud DS 1.1.152
   *
   * One received message in a `BfMessageList`. The row IS the target: a `<button>` that opens
   * the whole record (a `BfModal record`), and it carries no actions, menu, checkbox or trailing
   * control of its own. Anything the user can do to a message is done in that dialog.
   *
   * `media`, `preview` and `meta` are optional by omission. The subject and the preview each
   * stay on one line and truncate — the contract, not a fallback: the register is scanned, and
   * the whole message is one click away.
   *
   * `date` is shown as given: pass it through `formatDate` / `formatDateTime` from `@/utils`.
   */
  withDefaults(
    defineProps<{
      /** The message's subject. */
      subject: string
      /** The first line of its content, verbatim. */
      preview?: string
      /** Already formatted by the date utility. */
      date?: string
    }>(),
    { preview: '', date: '' },
  )

  defineEmits<{
    click: [event: MouseEvent]
  }>()
</script>

<template>
  <button type="button" class="message-row" @click="$emit('click', $event)">
    <span v-if="$slots.media" class="message-row__media"><slot name="media" /></span>

    <span class="message-row__text">
      <span class="message-row__subject">{{ subject }}</span>
      <span v-if="preview" class="message-row__preview">{{ preview }}</span>
    </span>

    <span v-if="$slots.meta" class="message-row__meta"><slot name="meta" /></span>

    <span v-if="date" class="message-row__date">{{ date }}</span>
  </button>
</template>

<style scoped lang="scss">
  /* The rule between rows is drawn ABOVE every row but the first, where the DS card draws it
     below every row but the last. Same lines on screen, but this way a row needs no knowledge
     of what follows it: the pager docked after the last row brings its own --bf-line top edge,
     so no double rule forms where the two meet. --bf-grey-light is the intra-table hairline. */
  .message-row {
    position: relative;
    display: flex;
    align-items: center;
    gap: var(--bf-sp-3);
    width: 100%;
    margin: 0;
    padding: 16px 20px;
    border: 0;
    border-radius: 0;
    background: var(--bf-white);
    font-family: var(--bf-font-body);
    text-align: left;
    cursor: pointer;
    transition: background var(--bf-dur-base) var(--bf-ease);
  }

  .message-row + .message-row {
    border-top: 1px solid var(--bf-grey-light);
  }

  /* Hover marker: the 3px --bf-orange-dark leading edge the console sidebar and the data table
     use. A pseudo-element, so the row's text never shifts on hover. */
  .message-row::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 3px;
    background: var(--bf-orange-dark);
    opacity: 0;
    transition: opacity var(--bf-dur-base) var(--bf-ease);
  }

  .message-row:hover {
    background: var(--bf-blue-light);
  }

  .message-row:hover::before,
  .message-row:focus-visible::before {
    opacity: 1;
  }

  /* Inset: the row is flush with the list's edge, and an outset ring would be clipped by the
     list's overflow: hidden. */
  .message-row:focus-visible {
    outline: 2px solid var(--bf-orange-dark);
    outline-offset: -2px;
  }

  .message-row__media {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
  }

  /* On hover the frame takes .icon-frame--tinted's own fill and --bf-orange-mid's hairline — a
     class cannot be added on hover, so its values are restated. Glyph, size and radius stay
     the frame's. */
  .message-row:hover :deep(.icon-frame) {
    border-color: var(--bf-orange-mid);
    background: var(--bf-orange-ultralight);
  }

  /* 12rem is the record row's text basis, so both registers wrap at the same width. */
  .message-row__text {
    display: flex;
    flex: 1 1 12rem;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
  }

  .message-row__subject {
    overflow: hidden;
    font-family: var(--bf-font-display);
    font-size: var(--bf-fs-sm);
    font-weight: var(--bf-fw-semibold);
    line-height: var(--bf-lh-snug);
    letter-spacing: var(--bf-tr-normal);
    color: var(--bf-fg-1);
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .message-row__preview {
    overflow: hidden;
    font-size: var(--bf-fs-xs_5);
    line-height: var(--bf-lh-normal);
    color: var(--bf-fg-2);
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  /* One badge — never a counter, bare text or a lone icon. */
  .message-row__meta {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
  }

  .message-row__date {
    flex: 0 0 auto;
    font-family: var(--bf-font-display);
    font-size: var(--bf-fs-2xs);
    font-weight: var(--bf-fw-semibold);
    line-height: var(--bf-lh-snug);
    font-variant-numeric: tabular-nums;
    letter-spacing: var(--bf-tr-caps-sm);
    color: var(--bf-fg-2);
    text-align: right;
    text-transform: uppercase;
    white-space: nowrap;
  }

  /* --bf-bp-sm (600px), measured on the LIST (a container query): the badge goes, and the date
     drops under the text, left-aligned. */
  @container (max-width: 600px) {
    .message-row {
      flex-wrap: wrap;
      padding: 14px 16px;
    }

    .message-row__meta {
      display: none;
    }

    .message-row__date {
      width: 100%;
      text-align: left;
    }
  }
</style>
